import { Box, Typography } from '@mui/material';
import {
    ChatBubbleOutlineOutlined as CommentIcon,
    AutoStoriesOutlined as StoryIcon,
    ForumOutlined as DmIcon,
} from '@mui/icons-material';
import type { ReactNode } from 'react';
import type { TriggerType } from '@/types/dmAutomation';

// AppBar 의 `deeper-orange` palette 와 일치 (defaultTheme.tsx 참고)
export const ACCENT = '#FB6D48';
export const ACCENT_HOVER = '#E55A3B';
export const ACCENT_TINT = 'rgba(251,109,72,0.08)';
export const ACCENT_TINT_STRONG = 'rgba(251,109,72,0.15)';

export const TRIGGER_LABEL: Record<TriggerType, string> = {
    COMMENT_KEYWORD: '피드 댓글+DM 자동화',
    STORY_REPLY: '스토리 댓글 자동화',
    DM_KEYWORD: 'DM 답장 자동화',
};

export const TRIGGER_ICON: Record<TriggerType, typeof CommentIcon> = {
    COMMENT_KEYWORD: CommentIcon,
    STORY_REPLY: StoryIcon,
    DM_KEYWORD: DmIcon,
};

// 트리거 한눈 식별용 — 좌측 strip / 아이콘 / 칩 색상으로 공유
export const TRIGGER_COLOR: Record<TriggerType, { main: string; tint: string }> = {
    COMMENT_KEYWORD: { main: '#1976d2', tint: 'rgba(25,118,210,0.12)' }, // 파랑 — 피드 댓글+DM
    STORY_REPLY: { main: '#c2185b', tint: 'rgba(194,24,91,0.12)' }, // 마젠타 — 스토리 답장
    DM_KEYWORD: { main: '#6a1b9a', tint: 'rgba(106,27,154,0.12)' }, // 보라 — DM 답장
};

export const parseKeywords = (keyword: string | null | undefined): string[] => {
    if (!keyword) return [];
    return keyword
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
};

export const parseTargetPostIds = (targetPost: string | null | undefined): string[] => {
    if (!targetPost || targetPost === 'ALL') return [];
    return targetPost
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
};

export const formatDate = (iso: string): string => {
    try {
        return iso.slice(0, 10);
    } catch {
        return iso;
    }
};

export const calcRate = (numerator: number, denominator: number): number => {
    if (!denominator) return 0;
    return Math.round((numerator / denominator) * 100);
};

interface PageHeaderProps {
    title: string;
    desc?: string;
    action?: ReactNode;
}

export const PageHeader = ({ title, desc, action }: PageHeaderProps) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                {title}
            </Typography>
            {desc && (
                <Typography variant="body2" color="text.secondary">
                    {desc}
                </Typography>
            )}
        </Box>
        {action}
    </Box>
);
